import fontKit from '@pdf-lib/fontkit'
import { PDFDocument, rgb, PDFPage, PDFFont, RGB } from 'pdf-lib'
import { getFile } from '@/utils/helpers'
import { keepArray } from 'best-modules/utils'

const WATERMARK_SCALE = 0.3

function drawWrappedCenteredText(options: {
  page: PDFPage
  text: string
  x: number
  y: number
  maxWidth: number
  font: PDFFont
  fontSize: number
  lineHeight?: number
  color: RGB
}): void {
  const {
    page,
    text,
    x,
    y,
    maxWidth,
    font,
    fontSize,
    lineHeight = fontSize + 2,
    color,
  } = options

  const words = text.split(' ').filter(Boolean)
  let lines = []
  let currentLine = ''

  for (const wordIdx in words) {
    const word = words[wordIdx]
    const testLine = currentLine ? currentLine + ' ' + word : word
    const testWidth = font.widthOfTextAtSize(testLine, fontSize)

    // У першої строки ширина меньше із-за заокруглення
    const lineMaxWidth = !lines.length ? maxWidth * 0.6 : maxWidth

    if (testWidth <= lineMaxWidth) {
      currentLine = testLine
    } else {
      // строка заповнена — фіксуєм і починаємо нову
      if (currentLine) {
        lines.push(currentLine)
      }
      currentLine = word
    }
  }

  if (currentLine) lines.push(currentLine)
  lines = lines.filter(Boolean)

  // ширина блока з текстом
  const textHeight = lines.length * lineHeight
  // висота блока з текстом
  const textWidth = font.widthOfTextAtSize(
    lines.reduce((acc, line) => (acc.length > line.length ? acc : line), ''),
    fontSize
  )

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const lineWidth = font.widthOfTextAtSize(line, fontSize)

    page.drawText(line, {
      x: x - textWidth / 2 + (maxWidth - lineWidth) / 2, // центрування
      y: y + (textHeight / 2 - lineHeight * 0.5) - i * lineHeight, // центрування
      size: fontSize,
      font,
      color,
    })
  }
}

async function getPdfWithWatermark(
  pdfBlob: Blob,
  watermarks: Array<{ text: string; isStamp: boolean }>,
  token: string
): Promise<Blob> {
  const stampBlob: Blob = await getFile({
    path: 'stamp/stamp15.png',
    type: 'blob',
    token,
  })
  const signBlob: Blob = await getFile({
    path: 'stamp/sign15.png',
    type: 'blob',
    token,
  })

  const fontBlob: Blob = await getFile({
    path: 'font/Arial.ttf',
    type: 'blob',
    token,
  })

  const pdfArrayBuffer = await pdfBlob.arrayBuffer()
  const stampArrayBuffer = await stampBlob.arrayBuffer()
  const signArrayBuffer = await signBlob.arrayBuffer()
  const fontArrayBuffer = await fontBlob.arrayBuffer()

  const pdfDoc = await PDFDocument.load(pdfArrayBuffer)
  const stampImage = await pdfDoc.embedPng(stampArrayBuffer)
  const signImage = await pdfDoc.embedPng(signArrayBuffer)
  const stampDims = stampImage.scale(WATERMARK_SCALE)
  const signDims = signImage.scale(WATERMARK_SCALE)
  pdfDoc.registerFontkit(fontKit)
  const font = await pdfDoc.embedFont(fontArrayBuffer, { subset: true })

  for (const page of pdfDoc.getPages()) {
    const { width: pageWidth } = page.getSize()
    const watermarkList = keepArray(watermarks)

    for (const watermarkIdx in watermarkList) {
      const watermark = watermarkList[watermarkIdx]
      const pngImage = watermark.isStamp ? stampImage : signImage
      const pngDims = watermark.isStamp ? stampDims : signDims

      const marginRight = 20
      const marginBottom = 20
      const offsetY = pngDims.height * Math.floor(Number(watermarkIdx) / 4)
      const offsetX = pngDims.width * (Number(watermarkIdx) % 4)

      page.drawImage(pngImage, {
        x: pageWidth - pngDims.width - marginRight - offsetX,
        y: marginBottom + offsetY,
        width: pngDims.width,
        height: pngDims.height,
      })

      drawWrappedCenteredText({
        page,
        text: watermark.text,
        x: pageWidth - pngDims.width * 0.5 - marginRight - offsetX,
        y: marginBottom + pngDims.width * 0.5 + offsetY,
        maxWidth: pngDims.width * 0.5,
        font,
        fontSize: pngDims.width * 0.05,
        color: watermark.isStamp
          ? rgb(0, 0, 251 / 255)
          : rgb(252 / 255, 137 / 255, 9 / 255),
      })
    }
  }

  const watermarkedBytes = await pdfDoc.save()

  return new Blob([watermarkedBytes], {
    type: 'application/pdf',
  })
}

export { getPdfWithWatermark }
